import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkCheck, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { studioApi, errMessage } from "@/lib/studioApi";

/** Saved name plates — reused across shows (auto-filled for returning guests). */
export function PlateLibrary() {
  const [plates, setPlates] = useState(null);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState({ name: "", title: "", org: "" });
  const [busy, setBusy] = useState(false);

  const load = (query = q) => studioApi.listPlates(query).then(setPlates).catch(() => setPlates([]));
  useEffect(() => { const id = setTimeout(() => load(q), 200); return () => clearTimeout(id); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await studioApi.savePlate(draft); setDraft({ name: "", title: "", org: "" }); toast.success("Plate saved"); load(); } catch (err) { toast.error(errMessage(err)); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove the saved plate for ${p.name}?`)) return;
    try { await studioApi.deletePlate(p.plate_id); load(); } catch (err) { toast.error(errMessage(err)); }
  };

  return (
    <section data-testid="plate-library">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Name plate library</h2>
          <p className="text-xs text-muted-foreground">Names, titles and organizations you've used on screen. Returning guests are filled in automatically; edit any guest in the console to update theirs.</p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plates" className="h-9 w-56 pl-8" data-testid="plate-search" />
        </div>
      </div>
      <div className="rounded-xl border bg-card">
        <form onSubmit={add} className="grid gap-2 border-b p-3 sm:grid-cols-[1fr_1fr_1fr_auto]" data-testid="plate-add-form">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" required className="h-9" data-testid="plate-add-name" />
          <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Title" className="h-9" data-testid="plate-add-title" />
          <Input value={draft.org} onChange={(e) => setDraft({ ...draft, org: e.target.value })} placeholder="Organization" className="h-9" data-testid="plate-add-org" />
          <Button type="submit" size="sm" className="h-9" disabled={busy || !draft.name.trim() || !(draft.title.trim() || draft.org.trim())} data-testid="plate-add-submit"><Plus className="mr-1 h-4 w-4" /> Save</Button>
        </form>
        {plates === null ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : plates.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="plates-empty">No saved plates yet. They're added automatically when you edit a guest's details in the console.</div>
        ) : (
          <ul className="divide-y" data-testid="plates-list">
            {plates.map((p) => (
              <li key={p.plate_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`plate-${p.plate_id}`}>
                <BookmarkCheck className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{[p.title, p.org].filter(Boolean).join(" · ")}{p.uses > 1 ? ` · used ${p.uses}×` : ""}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(p)} title="Remove" data-testid={`plate-delete-${p.plate_id}`}><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default PlateLibrary;
